import { get, remove, set } from 'lib/storage/versionedLocalStorageUtils'
import { getCurrentFlightDateChangeSearchEntry } from 'checkout/selectors/flightDateChangeSelectors'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { sendErrorToSentry } from 'lib/utils/sentryUtils'

const STORAGE_KEY = 'flightDateChangeProgress'
// Increment the version when the persisted wizard/search shape changes incompatibly.
const STORAGE_VERSION = 1
const SESSION_DURATION = FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

export function saveFlightDateChangeProgress(state: App.State) {
  const { cart, isCartRestored } = state.checkout
  if (state.checkout.processed) {
    remove(STORAGE_KEY)
    return
  }
  // Startup must not overwrite the draft before checkout has read it.
  if (!isCartRestored) return
  if (cart.mode !== 'flight-change-dates' || !cart.existingOrder || cart.isAbandonedCart) {
    remove(STORAGE_KEY)
    return
  }

  const wizard = state.flights.flightsDateChange
  const entry = getCurrentFlightDateChangeSearchEntry(state)
  const search = entry?.search
  // Keep only the current completed search. Its fares and pairing data must share
  // a search ID with any partially selected flights after a refresh.
  const searches = entry && search?.searchId && !search.isFetching && !search.error ? { [entry.key]: search } : {}
  const progress: App.FlightDateChangeProgress = {
    cartId: cart.cartId,
    orderId: cart.existingOrder.id,
    wizard,
    searches,
  }
  try {
    set(STORAGE_KEY, STORAGE_VERSION, progress)
  } catch(error) {
    // A large search can exhaust browser storage. Keep the smaller wizard draft
    // and let restoration fetch fares again, without interrupting checkout.
    sendErrorToSentry(error)
    try {
      set(STORAGE_KEY, STORAGE_VERSION, { ...progress, searches: {}, wizard: { ...wizard, flightSelection: undefined } })
    } catch {
      remove(STORAGE_KEY)
    }
  }
}

export function getSavedFlightDateChangeProgress(cart: Partial<App.CheckoutCartState>): App.FlightDateChangeProgress | undefined {
  if (cart.mode !== 'flight-change-dates' || !cart.existingOrder) return
  let progress: App.FlightDateChangeProgress | null
  try {
    progress = get(STORAGE_KEY, STORAGE_VERSION)
  } catch(error) {
    sendErrorToSentry(error)
    return undefined
  }
  if (!progress || progress.cartId !== cart.cartId || progress.orderId !== cart.existingOrder.id) return

  const searches = Object.fromEntries(Object.entries(progress.searches).filter(([, search]) =>
    search?.timestamp && Date.now() - search.timestamp < SESSION_DURATION,
  ))

  return {
    ...progress,
    searches,
    wizard: {
      ...progress.wizard,
      // Expired/incomplete searches must be requested again. Keep dates and the
      // current step, but never combine old selected fares with a new search ID.
      flightSelection: Object.keys(searches).length ? progress.wizard.flightSelection : undefined,
    },
  }
}
